/**
 * apps/katalog-app/src/hooks/useAuthDrawer.tsx
 *
 * AuthDrawerContext — global mechanism for gated actions.
 *
 * Usage:
 *   const { requireAuth } = useAuthDrawer();
 *   <button onClick={requireAuth(() => addToCart(item))}>Buy</button>
 *
 * If logged in → runs action immediately.
 * If logged out → opens auth drawer, binds InviteIQ on success, then runs action.
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from '@bright-web-skeleton/auth';
import { supabase } from '@bright-web-skeleton/supabase';
import { bindPendingInvite, getPendingInviteIQ } from '@bright-web-skeleton/shared/inviteiq';

// ── Types ──────────────────────────────────────────────────

type PendingAction = () => void | Promise<void>;

interface AuthDrawerState {
  open: boolean;
  openDrawer: (pendingAction?: PendingAction) => void;
  closeDrawer: () => void;
  onAuthSuccess: (userId: string) => Promise<void>;
  /** Gate an action behind auth. If logged in, runs immediately. Otherwise opens drawer. */
  requireAuth: (action: PendingAction) => () => void;
  /** Set the current surface katalog ID (for acquisition attribution on signup) */
  setSurfaceKatalogId: (id: string | null) => void;
}

const AuthDrawerContext = createContext<AuthDrawerState | undefined>(undefined);

// ── Provider ───────────────────────────────────────────────

export const AuthDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authUser } = useAuth();
  const [open, setOpen] = useState(false);
  const pendingActionRef = useRef<PendingAction | null>(null);
  const inviteBindCalledRef = useRef(false);
  const surfaceKatalogIdRef = useRef<string | null>(null);

  const openDrawer = useCallback((action?: PendingAction) => {
    pendingActionRef.current = action ?? null;
    setOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    pendingActionRef.current = null;
    setOpen(false);
  }, []);

  const onAuthSuccess = useCallback(async (userId: string) => {
    // Bind InviteIQ once per session
    if (!inviteBindCalledRef.current) {
      inviteBindCalledRef.current = true;
      try {
        const result = await bindPendingInvite(supabase, userId, surfaceKatalogIdRef.current);

        if (result.status === 'inactive') {
          // Invite was disabled — keep pending in storage so user can retry later
          console.warn('[AuthDrawer] Invite is inactive, keeping pending code');
          inviteBindCalledRef.current = false; // allow retry
        }
      } catch (err) {
        console.warn('[AuthDrawer] InviteIQ bind failed:', err);
      }
    }

    // Close drawer
    setOpen(false);

    // Run the stored action
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action) {
      try {
        await action();
      } catch (err) {
        console.error('[AuthDrawer] Pending action failed:', err);
      }
    }

    // Post-auth redirect based on invite label stored in localStorage
    // Label format: "deal:<id>", "katalog:<id>"
    try {
      const pendingCode = getPendingInviteIQ();
      // If there's no pending action and no pending code, no redirect needed
      if (!action && pendingCode) {
        // The label metadata is not directly available from localStorage,
        // but the URL ?iq= capture already happened and the user is now on the page
        // they were visiting when auth was triggered. No additional redirect needed
        // since the page itself handles the context.
      }
    } catch {
      // Ignore
    }
  }, []);

  const requireAuth = useCallback(
    (action: PendingAction) => {
      return () => {
        if (authUser) {
          // Already logged in — run immediately
          action();
        } else {
          // Open drawer, store action
          openDrawer(action);
        }
      };
    },
    [authUser, openDrawer],
  );

  const setSurfaceKatalogId = useCallback((id: string | null) => {
    surfaceKatalogIdRef.current = id;
  }, []);

  return (
    <AuthDrawerContext.Provider value={{ open, openDrawer, closeDrawer, onAuthSuccess, requireAuth, setSurfaceKatalogId }}>
      {children}
    </AuthDrawerContext.Provider>
  );
};

// ── Hook ───────────────────────────────────────────────────

export function useAuthDrawer(): AuthDrawerState {
  const ctx = useContext(AuthDrawerContext);
  if (!ctx) throw new Error('useAuthDrawer must be used within AuthDrawerProvider');
  return ctx;
}
